import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { createInvites, getActiveConference, } from "./magicLinkApi";
// Contact import is intentionally hidden until explicitly re-enabled.
// import ContactImportButton from "./ContactImportButton";
import { parseEntries as _parseEntries } from "./phoneParser";
import { useChatSecurity } from "../provider/ChatSecurityProvider";
import { SecureChatInviteIcon, SecureLockIcon, SendIcon, } from "../components/callIcons";
import { useOwnedScrollLock } from "../utils/ownedScrollLock";
import "./QuickInviteDialog.css";
/** Re-exported for unit testing. */
export const parseEntries = _parseEntries;
// Bound connection UI waits without ever retrying an invitation POST.
function waitForConnection(request) {
    let timer;
    return Promise.race([
        request,
        new Promise((_, reject) => {
            timer = setTimeout(() => reject(new Error("connection_timeout")), 20_000);
        }),
    ]).finally(() => clearTimeout(timer));
}
/**
 * Read a contentEditable field without losing the visual line breaks created
 * by Enter. textContent flattens nested div/br rows in some browsers, which
 * can turn two phone numbers into one invalid recipient.
 */
function readEditableText(root) {
    let value = "";
    const appendBreak = () => {
        if (value && !value.endsWith("\n"))
            value += "\n";
    };
    const visit = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            value += node.textContent || "";
            return;
        }
        if (!(node instanceof HTMLElement))
            return;
        if (node.tagName === "BR") {
            appendBreak();
            return;
        }
        const isBlockRow = /^(DIV|P|LI)$/.test(node.tagName);
        if (isBlockRow)
            appendBreak();
        node.childNodes.forEach(visit);
        if (isBlockRow)
            appendBreak();
    };
    root.childNodes.forEach(visit);
    return value.replace(/\n{3,}/g, "\n\n").trim();
}
/** Returns a human-readable label for an email: real phone number for
 *  synthetic "+phone@sms.invalid" keys, the address itself otherwise. */
function humanizeEmail(email) {
    if (email.endsWith("@sms.invalid"))
        return email.split("@")[0];
    return email;
}
/**
 * Compact "add a guest" popup for the chat overlay: the admin enters one or
 * more e-mails and sends invites without leaving the chat.
 *
 * - With an active conference the invitees join it; the server extends a
 *   mid-meeting invitee's join window to the conference end, so the link
 *   works immediately.
 * - Without one, a new chat-scope conference is created starting now (2h
 *   window) — the guests land in this wallet's chat room.
 *
 * Templates and fine-grained windows stay on the Invitations page; this
 * dialog deliberately ships only the fast path with the default template.
 */
const QuickInviteDialog = ({ hostWallet, hostEmail, onClose, onInvited }) => {
    const { t, i18n } = useTranslation();
    const security = useChatSecurity();
    const deviceSessionToken = security.wallet === hostWallet.toLowerCase() ? security.deviceSessionToken : null;
    const sessionReady = security.status === "ready" && !!deviceSessionToken;
    const binding = `${hostWallet.toLowerCase()}:${deviceSessionToken || ""}:${security.status}`;
    const currentBinding = useRef(binding);
    currentBinding.current = binding;
    const mounted = useRef(true);
    const sending = useRef(false);
    const [rejectedBinding, setRejectedBinding] = useState(null);
    const [loadedBinding, setLoadedBinding] = useState(null);
    const [lookupFailed, setLookupFailed] = useState(false);
    const [reconnecting, setReconnecting] = useState(false);
    const [reload, setReload] = useState(0);
    const sessionRejected = rejectedBinding === binding;
    const canSend = sessionReady && !sessionRejected && loadedBinding === binding && !reconnecting;
    const [conference, setConference] = useState(null);
    const [loading, setLoading] = useState(true);
    const [emailsText, setEmailsText] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [smsFailedLinks, setSmsFailedLinks] = useState([]);
    const [smsFailedNoLink, setSmsFailedNoLink] = useState(false);
    const [copiedLink, setCopiedLink] = useState(null);
    const dialogRef = useRef(null);
    const recipientsRef = useRef(null);
    const closeButtonRef = useRef(null);
    const sendButtonRef = useRef(null);
    // The dialog is portaled below, but its background owner is still the
    // production mobile .mobile-content scroller. Keep that owner locked for
    // the complete lifetime of this instance; the owned lock is ref-counted so
    // nested contact/template dialogs cannot unlock one another.
    useOwnedScrollLock(true);
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);
    const isSessionError = (e) => [
        "wallet_device_session_required", "invalid_or_expired_wallet_device_session",
        "wallet_device_session_origin_mismatch", "wallet_device_session_host_mismatch",
    ].includes(e?.code || e?.message);
    useEffect(() => {
        let cancelled = false;
        setConference(null);
        setLoadedBinding(null);
        setLookupFailed(false);
        setError(null);
        setNotice(null);
        setSmsFailedLinks([]);
        setSmsFailedNoLink(false);
        if (!sessionReady || sessionRejected) {
            setLoading(false);
            return () => { cancelled = true; };
        }
        setLoading(true);
        waitForConnection(getActiveConference(hostWallet, deviceSessionToken))
            .then((conf) => {
            if (!cancelled && currentBinding.current === binding) {
                setConference(conf);
                setLoadedBinding(binding);
            }
        })
            .catch((e) => {
            if (cancelled || currentBinding.current !== binding)
                return;
            if (isSessionError(e))
                setRejectedBinding(binding);
            else
                setLookupFailed(true);
        })
            .finally(() => { if (!cancelled)
            setLoading(false); });
        return () => { cancelled = true; };
    }, [hostWallet, deviceSessionToken, binding, sessionReady, sessionRejected, reload]);
    const handleReconnect = async () => {
        if (reconnecting || busy || sending.current)
            return;
        setReconnecting(true);
        setError(null);
        try {
            // Never resend an invitation automatically. Renewal/provisioning happens
            // only after this explicit user action; a new send still needs a click.
            if (!sessionReady || sessionRejected) {
                if (security.wallet === hostWallet.toLowerCase() && security.identity) {
                    await waitForConnection(security.renewDeviceSession());
                }
                else {
                    await waitForConnection(security.retryProvision());
                }
            }
            if (!mounted.current)
                return;
            if (currentBinding.current === binding)
                setRejectedBinding(null);
            setReload(value => value + 1);
        }
        catch {
            if (mounted.current && currentBinding.current === binding) {
                setError(t("magicLink.quickInvite.connectionFailed"));
            }
        }
        finally {
            if (mounted.current)
                setReconnecting(false);
        }
    };
    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog)
            return undefined;
        const previousFocus = document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;
        const initialField = dialog.querySelector("#quick-invite-recipients:not(:disabled)");
        (initialField || dialog).focus();
        return () => {
            previousFocus?.focus();
        };
    }, []);
    useEffect(() => {
        const dialog = dialogRef.current;
        const recipients = dialog?.querySelector("#quick-invite-recipients");
        if (!loading && dialog && recipients && document.activeElement === dialog) {
            recipients.focus();
        }
    }, [loading]);
    const handleDialogKeyDown = (event) => {
        if (event.key === "Escape") {
            event.preventDefault();
            onClose();
            return;
        }
        if (event.key !== "Tab")
            return;
        const first = closeButtonRef.current;
        const last = sendButtonRef.current;
        if (!first || !last) {
            event.preventDefault();
            event.currentTarget.focus();
            return;
        }
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        }
        else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    };
    const handleCopyLink = (link) => {
        navigator.clipboard.writeText(link).then(() => {
            setCopiedLink(link);
            setTimeout(() => setCopiedLink(null), 2000);
        }).catch(() => { });
    };
    const handleSend = async () => {
        if (!canSend || busy || loading || sending.current)
            return;
        setError(null);
        setNotice(null);
        setSmsFailedLinks([]);
        setSmsFailedNoLink(false);
        const entries = parseEntries(emailsText);
        if (entries.length === 0) {
            setError(t("magicLink.invite.errorNoRecipients"));
            return;
        }
        const emailEntries = entries.filter((e) => !!e.email);
        const phoneOnlyEntries = entries.filter((e) => !e.email && !!e.phone);
        const invalid = emailEntries.filter((e) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.email));
        if (invalid.length > 0) {
            setError(t("magicLink.invite.errorInvalidEmails", { emails: invalid.map((e) => e.email).join(", ") }));
            return;
        }
        const names = {};
        for (const e of emailEntries) {
            if (e.name && e.email)
                names[e.email.trim().toLowerCase()] = e.name;
        }
        const phones = {};
        for (const e of emailEntries) {
            if (e.phone && e.email)
                phones[e.email.trim().toLowerCase()] = e.phone;
        }
        // Running conference: reuse its window (the server extends the join
        // window mid-meeting). Otherwise: a fresh 2-hour conference from now.
        const now = Date.now();
        const fromIso = conference ? conference.validFrom : new Date(now).toISOString();
        const untilIso = conference ? conference.validUntil : new Date(now + 2 * 60 * 60 * 1000).toISOString();
        const joinIso = conference ? conference.joinUntil : untilIso;
        sending.current = true;
        setBusy(true);
        const isCurrent = () => mounted.current && currentBinding.current === binding;
        const refreshConference = () => {
            getActiveConference(hostWallet, deviceSessionToken).then(conf => {
                if (isCurrent())
                    setConference(conf);
            }).catch(() => { });
        };
        try {
            const resp = await createInvites({
                hostWallet,
                emails: emailEntries.map((e) => e.email),
                phoneOnlyEntries: phoneOnlyEntries.length > 0
                    ? phoneOnlyEntries.map((e) => ({ phone: e.phone, name: e.name }))
                    : undefined,
                validFrom: fromIso,
                validUntil: untilIso,
                joinUntil: joinIso,
                // Guests always get the full dashboard (chat-only mode retired —
                // invites stay on the Invitations page with its explicit controls.
                scope: "full",
                names: Object.keys(names).length > 0 ? names : undefined,
                phones: Object.keys(phones).length > 0 ? phones : undefined,
                // Organizer calendar-sync mail only for a NEW conference.
                hostEmail: !conference && hostEmail ? hostEmail : undefined,
                emailLocale: i18n?.resolvedLanguage === "de" ? "de" : "en",
            }, deviceSessionToken);
            if (!isCurrent())
                return;
            setEmailsText("");
            if (recipientsRef.current)
                recipientsRef.current.textContent = "";
            // For phone-only invites, "success" means SMS was attempted;
            // email-based invites must have emailSent=true.
            const emailInvites = resp.invites.filter((i) => !i.phoneOnly);
            const phoneInvites = resp.invites.filter((i) => i.phoneOnly);
            const emailFailed = emailInvites.filter((i) => !i.emailSent).length;
            const smsFailed = phoneInvites.filter((i) => i.smsSent === false).length;
            const notSent = emailFailed + smsFailed;
            if (smsFailed > 0) {
                const links = phoneInvites
                    .filter((i) => i.smsSent === false && i.link)
                    .map((i) => i.link);
                setSmsFailedLinks(links);
                if (phoneInvites.some((i) => i.smsSent === false && !i.link)) {
                    setSmsFailedNoLink(true);
                }
            }
            if (notSent > 0) {
                setError(t("magicLink.quickInvite.sentButDeliveryFailed", { count: notSent }));
            }
            else {
                setNotice(t("magicLink.quickInvite.sent", { count: resp.invites.length }));
            }
            if (!conference) {
                // The just-created conference becomes the active one — further sends
                // from this dialog must join it instead of failing with a conflict.
                refreshConference();
            }
            onInvited?.();
        }
        catch (e) {
            if (!isCurrent())
                return;
            if (isSessionError(e)) {
                setRejectedBinding(binding);
            }
            else if (e?.code === "conference_active") {
                // The lookup missed a conference that exists server-side (lookup
                // failure or race) — reload the authoritative state so the next send
                // joins that conference instead of failing again.
                setError(t("magicLink.quickInvite.errorConferenceActive"));
                refreshConference();
            }
            else if (e?.code === "conference_join_closed") {
                setError(t("magicLink.quickInvite.errorJoinClosed"));
                refreshConference();
            }
            else if (e?.code === "duplicate_email") {
                setError(t("magicLink.invite.errorDuplicate", { emails: (e.details?.emails || []).map(humanizeEmail).join(", ") }));
            }
            else {
                setError(e?.message || t("magicLink.quickInvite.errorSend"));
            }
        }
        finally {
            sending.current = false;
            if (mounted.current)
                setBusy(false);
        }
    };
    const dialog = (_jsx("div", { className: "quick-invite-overlay", onClick: onClose, role: "presentation", children: _jsxs("div", { ref: dialogRef, className: "quick-invite-dialog", onClick: (e) => e.stopPropagation(), onKeyDown: handleDialogKeyDown, role: "dialog", "aria-modal": "true", "aria-labelledby": "quick-invite-title", tabIndex: -1, children: [_jsxs("div", { className: "quick-invite-header", children: [_jsxs("h2", { id: "quick-invite-title", className: "quick-invite-title", children: [_jsx(SecureChatInviteIcon, {}), " ", t("magicLink.quickInvite.title")] }), _jsx("button", { ref: closeButtonRef, type: "button", className: "quick-invite-close", onClick: onClose, "aria-label": t("common.buttons.back"), children: t("common.buttons.back") })] }), _jsxs("div", { className: "quick-invite-body", children: [_jsx("p", { className: "quick-invite-hint", children: loading
                                ? t("magicLink.quickInvite.loading")
                                : conference
                                    ? t("magicLink.quickInvite.hintActive")
                                    : t("magicLink.quickInvite.hintNew") }), _jsx("label", { id: "quick-invite-recipients-label", className: "quick-invite-label", htmlFor: "quick-invite-recipients", children: t("magicLink.quickInvite.recipientsLabel") }), _jsx("div", { ref: recipientsRef, id: "quick-invite-recipients", className: "quick-invite-emails", contentEditable: !busy && !loading, role: "textbox", "aria-labelledby": "quick-invite-recipients-label", "aria-multiline": "true", "aria-disabled": busy || loading, "data-placeholder": t("magicLink.quickInvite.placeholder"), suppressContentEditableWarning: true, onInput: (e) => setEmailsText(readEditableText(e.currentTarget)) }), (!sessionReady || sessionRejected || lookupFailed || reconnecting) && (_jsxs("div", { className: "quick-invite-error", role: "status", children: [_jsx("p", { children: t(`magicLink.quickInvite.${sessionRejected ? "sessionExpired" : lookupFailed ? "connectionFailed" : security.status === "provisioning" || reconnecting ? "connecting" : "connectionRequired"}`) }), _jsx("button", { type: "button", className: "quick-invite-back", onClick: handleReconnect, disabled: reconnecting || busy, children: t(`magicLink.quickInvite.${reconnecting ? "connecting" : "reconnect"}`) })] })), error && _jsx("div", { className: "quick-invite-error", role: "alert", children: error }), smsFailedLinks.length > 0 && (_jsxs("div", { className: "quick-invite-sms-links", children: [_jsx("div", { className: "quick-invite-sms-links-label", children: t("magicLink.quickInvite.smsFailedCopyLink") }), smsFailedLinks.map((link) => (_jsxs("div", { className: "quick-invite-sms-link-row", children: [_jsx("span", { className: "quick-invite-sms-link-url", children: link }), _jsx("button", { type: "button", className: "quick-invite-sms-link-copy", onClick: () => handleCopyLink(link), children: copiedLink === link ? t("magicLink.quickInvite.linkCopied") : t("magicLink.quickInvite.copyLink") })] }, link)))] })), smsFailedNoLink && _jsx("div", { className: "quick-invite-sms-no-link", children: t("magicLink.quickInvite.smsFailedNoLink") }), notice && _jsx("div", { className: "quick-invite-notice", role: "status", children: notice })] }), _jsxs("div", { className: "quick-invite-footer", children: [_jsxs("button", { ref: sendButtonRef, type: "button", className: "quick-invite-send", onClick: handleSend, disabled: busy || loading || !canSend, children: [_jsx(SendIcon, {}), " ", busy ? t("magicLink.quickInvite.sending") : t("magicLink.quickInvite.send")] }), _jsxs("p", { className: "quick-invite-security-note", children: [_jsx(SecureLockIcon, {}), " ", t("magicLink.quickInvite.securityNote")] })] })] }) }));
    // The mobile call host is a sibling stacking context of MobileChatPage.
    // Rendering the invite inside that subtree lets the minimized call panel
    // paint over the form. A body portal gives the overlay one authoritative
    // layer without changing the approved active/minimized call markup.
    return typeof document === "undefined" ? dialog : createPortal(dialog, document.body);
};
export default QuickInviteDialog;
